import * as React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { ConnectDataSourceWizard } from '../../../domains/data-binding/components/ConnectDataSourceWizard';
import type { DataSourceProvider } from '../../../domains/data-binding/models/DataSource';
import {
  SpreadsheetIntentResolver,
  type SpreadsheetMappingReviewSelection,
  type SpreadsheetIntentResolution,
} from '../../../domains/knowledge-ingestion/services/SpreadsheetIntentResolver';
import { SourceIntentReviewPanel } from './SourceIntentReviewPanel';
import type { ConnectorManifest, FolderKind, ImportTarget } from '../connectorRegistry';
import {
  applyTabularImport,
  fetchAllConnectedSourceRecords,
  headersFromRecords,
  preferredProfileId,
  previewTabularImport,
  tabularImportLimitError,
  TabularImportApplyError,
  type TabularImportBatch,
} from '../services/tabularImport';
import {
  canConfirmIntentResolution,
  canConfirmMappingSelections,
  findSelectedIntentCandidate,
  initialMappingSelections,
  initialSelectedIntentForResolution,
} from '../services/intentReviewGate';
import { importableProfileFieldAliases, importableProfileFieldEntityRefs } from '../utils/importableProfileFieldAliases';
import { getDomainProfile } from '../profileRegistry';
import {
  connectedTabularSourceLabel,
  connectedTabularSourceTableId,
} from './ConnectedTabularSourcePresentation';

export interface ConnectedTabularSourceStageProps {
  readonly manifest: ConnectorManifest;
  readonly folderId: string;
  readonly folderKind: FolderKind;
  readonly target: ImportTarget;
  readonly onBack: () => void;
  readonly onComplete: (result: { createdCount: number; updatedCount: number }) => void;
  readonly onCancel: () => void;
  readonly onAddAnother?: () => void;
  readonly completionAction?: {
    readonly label: string;
    readonly onClick: () => void;
    readonly pending?: boolean;
    readonly disabled?: boolean;
  };
}

type Phase = 'selecting' | 'loading' | 'confirming' | 'reviewing' | 'applying' | 'success' | 'error';

interface DataSourceDetailResponse {
  readonly dataSource?: {
    readonly id: string;
    readonly provider: DataSourceProvider;
    readonly name: string;
    readonly credentialSourceId?: string | null;
    readonly config?: Record<string, unknown>;
    readonly sourceTableId?: string | null;
  };
}

export const ConnectedTabularSourceStage: React.FC<ConnectedTabularSourceStageProps> = ({
  manifest,
  folderId,
  folderKind,
  target,
  onBack,
  onComplete,
  onCancel,
  onAddAnother,
  completionAction,
}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const [phase, setPhase] = React.useState<Phase>('selecting');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [errorDetail, setErrorDetail] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<{
    createdCount: number;
    updatedCount: number;
    unresolvedCount?: number;
  } | null>(null);
  const [pendingImport, setPendingImport] = React.useState<TabularImportBatch | null>(null);
  const [selectedSource, setSelectedSource] =
    React.useState<{ id: string; sourceTableId: string | null } | null>(null);
  const [intentResolution, setIntentResolution] =
    React.useState<SpreadsheetIntentResolution | null>(null);
  const [selectedIntent, setSelectedIntent] = React.useState<string | null>(null);
  const [mappingSelections, setMappingSelections] =
    React.useState<SpreadsheetMappingReviewSelection>({});

  const provider = providerForManifest(manifest);
  const profileId = React.useMemo(
    () => preferredProfileId(manifest, folderKind),
    [folderKind, manifest],
  );
  const intentResolver = React.useMemo(() => new SpreadsheetIntentResolver(), []);
  const importableFieldAliases = React.useMemo(
    () => importableProfileFieldAliases(profileId),
    [profileId],
  );
  const importableFieldEntityRefs = React.useMemo(
    () => importableProfileFieldEntityRefs(profileId),
    [profileId],
  );
  const selectedIntentCandidate = React.useMemo(() => {
    return findSelectedIntentCandidate(intentResolution, selectedIntent);
  }, [intentResolution, selectedIntent]);
  const canConfirmIntent = React.useMemo(
    () =>
      canConfirmIntentResolution(intentResolution, selectedIntent) &&
      canConfirmMappingSelections(selectedIntentCandidate, mappingSelections),
    [
      intentResolution,
      mappingSelections,
      selectedIntent,
      selectedIntentCandidate,
    ],
  );
  const handleSelectIntent = React.useCallback(
    (intent: string) => {
      const candidate =
        intentResolution?.candidates.find(
          (intentCandidate) => intentCandidate.intent === intent,
        ) ?? null;
      setSelectedIntent(intent);
      setMappingSelections(
        candidate ? initialMappingSelections(candidate) : {},
      );
    },
    [intentResolution],
  );

  const clearReview = React.useCallback(() => {
    setPendingImport(null);
    setSelectedSource(null);
    setIntentResolution(null);
    setSelectedIntent(null);
    setMappingSelections({});
  }, []);

  const loadSourceRecords = React.useCallback(async (sourceId: string) => {
    if (!provider) {
      setPhase('error');
      setErrorMsg(`Unsupported tabular provider for ${manifest.label}.`);
      setErrorDetail(null);
      return;
    }

    setPhase('loading');
    setErrorMsg('');
    setErrorDetail(null);
    clearReview();

    try {
      const [sourceResp, fetched] = await Promise.all([
        authenticatedFetch(`/api/data-sources/${encodeURIComponent(sourceId)}`),
        fetchAllConnectedSourceRecords(authenticatedFetch, sourceId),
      ]);

      if (!sourceResp.ok) {
        const body = (await sourceResp.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `source lookup failed with status ${sourceResp.status}`);
      }
      const limitError = tabularImportLimitError({
        loaded: fetched.records.length,
        total: fetched.total,
        hasMore: fetched.truncated,
      });
      if (limitError) throw new Error(limitError);

      const sourceBody = (await sourceResp.json()) as DataSourceDetailResponse;
      const records = fetched.records;
      setSelectedSource({
        id: sourceId,
        sourceTableId: connectedTabularSourceTableId(sourceBody.dataSource),
      });
      const batch: TabularImportBatch = {
        sourceLabel: connectedTabularSourceLabel(sourceBody.dataSource, manifest.label),
        records,
      };
      const resolution = intentResolver.resolve({
        sourceSystem: manifest.source?.system ?? provider,
        folderKind,
        profileId,
        headers: headersFromRecords(records),
        sampleRows: records.slice(0, 10).map((record) => record.data),
        importableFieldAliases,
        importableFieldEntityRefs,
      });

      setPendingImport(batch);
      setIntentResolution(resolution);
      setSelectedIntent(initialSelectedIntentForResolution(resolution));
      setMappingSelections(initialMappingSelections(resolution.recommended));
      setPhase('confirming');
    } catch (err) {
      setPhase('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load source records');
      setErrorDetail(null);
    }
  }, [
    authenticatedFetch,
    clearReview,
    folderKind,
    importableFieldAliases,
    importableFieldEntityRefs,
    intentResolver,
    manifest.label,
    manifest.source?.system,
    profileId,
    provider,
  ]);

  const handleConfirmImport = React.useCallback(async () => {
    if (!pendingImport || !selectedIntentCandidate || !canConfirmIntent) return;
    setPhase('applying');
    setErrorMsg('');
    setErrorDetail(null);
    try {
      const mappingProfile = intentResolver.buildReviewedMappingProfile({
        candidate: selectedIntentCandidate,
        headers: headersFromRecords(pendingImport.records),
        selections: mappingSelections,
      });
      const preview = await previewTabularImport({
        fetcher: authenticatedFetch,
        folderId,
        manifest,
        dataSourceId: selectedSource?.id ?? null,
        sourceTableId: selectedSource?.sourceTableId ?? null,
        selectedIntent: selectedIntentCandidate.intent,
        profileId,
        target,
        batch: pendingImport,
        mappingProfile,
      });
      const out = await applyTabularImport({
        fetcher: authenticatedFetch,
        folderId,
        manifest,
        dataSourceId: selectedSource?.id ?? null,
        sourceTableId: selectedSource?.sourceTableId ?? null,
        selectedIntent: selectedIntentCandidate.intent,
        profileId,
        target,
        batch: pendingImport,
        mappingProfile,
        previewUploadId: preview.previewUploadId,
        acceptReviewedSchemaChange: true,
      });
      setResult(out);
      clearReview();
      setPhase('success');
      onComplete(out);
    } catch (err) {
      setPhase('error');
      if (err instanceof TabularImportApplyError) {
        setErrorMsg(err.message);
        setErrorDetail(err.detail ?? null);
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'apply failed');
        setErrorDetail(null);
      }
    }
  }, [
    authenticatedFetch,
    canConfirmIntent,
    clearReview,
    folderId,
    manifest,
    intentResolver,
    mappingSelections,
    onComplete,
    pendingImport,
    profileId,
    selectedSource,
    selectedIntentCandidate,
    target,
  ]);

  const isBusy = phase === 'loading' || phase === 'applying';
  const progressStep = phase === 'selecting' || phase === 'loading' ? 1 : phase === 'confirming' ? 2 : 3;
  const isSpreadsheet = provider === 'excel_online' || provider === 'google_sheets';

  return (
    <div className="flex flex-col gap-6" data-testid="tid.compass.connectedTabularStage">
      <ol aria-label="Import progress" className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-5 text-xs">
        {['Choose source', isSpreadsheet ? 'Choose worksheet' : 'Connect account', 'Map fields', 'Review & import'].map((label, index) => (
          <li key={label} aria-current={index === progressStep ? 'step' : undefined} className={`flex items-center gap-2 ${index === progressStep ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
            <span className={`flex size-5 items-center justify-center rounded-full text-[10px] ${index === progressStep ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              {index < progressStep ? <CheckCircle2 className="size-3.5" /> : index + 1}
            </span>
            {label}
          </li>
        ))}
      </ol>

      {provider ? (
        <div hidden={phase !== 'selecting'}>
        <ConnectDataSourceWizard
          embedded
          onBack={onBack}
          allowedProviders={[provider]}
          skipFieldMapping
          folderId={folderId}
          profile={getDomainProfile(folderKind) ?? undefined}
          onCancel={onCancel}
          onComplete={(sourceId) => {
            void loadSourceRecords(sourceId);
          }}
        />
        </div>
      ) : null}

      {isBusy ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-[color:var(--accent)]/30 px-3 py-2.5 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {phase === 'loading' ? 'Loading records...' : 'Adding to folder...'}
        </div>
      ) : null}

      {phase === 'confirming' && intentResolution && selectedIntentCandidate ? (
        <SourceIntentReviewPanel
          resolution={intentResolution}
          selectedCandidate={selectedIntentCandidate}
          selectedIntent={selectedIntent}
          headers={headersFromRecords(pendingImport?.records ?? [])}
          sampleRows={(pendingImport?.records ?? [])
            .slice(0, 10)
            .map((record) => record.data)}
          mappingSelections={mappingSelections}
          rowCount={pendingImport?.records.length ?? 0}
          columnCount={headersFromRecords(pendingImport?.records ?? []).length}
          onSelectIntent={handleSelectIntent}
          onMappingChange={(fieldId, sourceField) => {
            setMappingSelections((current) => ({
              ...current,
              [fieldId]: sourceField,
            }));
          }}
        />
      ) : null}

      {phase === 'reviewing' && pendingImport && selectedIntentCandidate ? (
        <div className="space-y-5" data-testid="tid.compass.connectedTabularStage.review">
          <div>
            <h3 className="text-base font-semibold">Ready to import</h3>
            <p className="mt-1 break-words text-sm text-muted-foreground">{pendingImport.sourceLabel}</p>
            <p className="mt-1 text-sm text-muted-foreground">{selectedIntentCandidate.label}</p>
          </div>
          <dl className="grid grid-cols-2 gap-4 border-y border-border py-4 text-sm">
            <div><dt className="text-muted-foreground">Rows</dt><dd className="mt-1 font-semibold">{pendingImport.records.length}</dd></div>
            <div><dt className="text-muted-foreground">Columns used</dt><dd className="mt-1 font-semibold">{new Set(Object.values(mappingSelections).filter(Boolean)).size}</dd></div>
          </dl>
          <div className="space-y-2 text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground">After import</h4>
            <p>Use Sync now in this folder’s Sources to pull in changes from {manifest.label}.</p>
            {folderKind === 'orgchart' && isSpreadsheet ? (
              <p>Atlas edits are submitted through Review changes. Writing back requires a supported mapping and this source to be the primary source. Imported data updates after the source confirms the changes.</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {phase === 'error' ? (
        <div
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
          data-testid="tid.compass.connectedTabularStage.error"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1 text-xs leading-snug text-red-900">
            {errorMsg}
            {errorDetail ? (
              <details className="mt-1.5">
                <summary className="cursor-pointer select-none text-[11px] font-medium text-red-800 hover:underline">
                  Technical details
                </summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-red-100/60 p-2 font-mono text-[10px] leading-relaxed text-red-900">
                  {errorDetail}
                </pre>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}

      {phase === 'success' && result ? (
        <div
          className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
          data-testid="tid.compass.connectedTabularStage.success"
        >
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-700" />
          <div className="flex-1 text-xs leading-snug text-emerald-900">
            Imported {result.createdCount} new record{result.createdCount === 1 ? '' : 's'}
            {result.updatedCount > 0 ? `, updated ${result.updatedCount}` : ''}.
            {(result.unresolvedCount ?? 0) > 0 ? (
              <div className="mt-1 text-amber-800">
                {result.unresolvedCount} row
                {result.unresolvedCount === 1 ? '' : 's'} could not be matched
                automatically — resolve them from this folder&apos;s Sources
                panel.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {phase !== 'selecting' && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-5">
        {phase !== 'success' && (
          <Button variant="ghost" size="sm" className="mr-auto" disabled={isBusy} onClick={() => {
            setPhase(phase === 'reviewing' ? 'confirming' : 'selecting');
          }}>
            <ArrowLeft className="mr-1.5 size-4" />Back
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onCancel}>
          {phase === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'success' && onAddAnother ? (
          <Button variant="outline" size="sm" onClick={onAddAnother}>
            Add another source
          </Button>
        ) : null}
        {phase === 'success' && result && completionAction ? (
          <Button
            size="sm"
            variant="navy"
            onClick={completionAction.onClick}
            disabled={completionAction.disabled || completionAction.pending}
            data-testid="tid.compass.connectedTabularStage.completionAction"
          >
            {completionAction.pending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : null}
            {completionAction.label}
          </Button>
        ) : null}
        {phase === 'confirming' ? (
          <Button size="sm" onClick={() => setPhase('reviewing')} disabled={!canConfirmIntent}>
            Review import
          </Button>
        ) : null}
        {phase === 'reviewing' ? (
          <Button size="sm" onClick={handleConfirmImport} disabled={!canConfirmIntent}>
            Confirm import
          </Button>
        ) : null}
      </div>}
    </div>
  );
};

function providerForManifest(manifest: ConnectorManifest): DataSourceProvider | null {
  if (manifest.id === 'google-sheets' || manifest.source?.system === 'google_sheets') {
    return 'google_sheets';
  }
  if (manifest.id === 'excel-online' || manifest.source?.system === 'excel') {
    return 'excel_online';
  }
  if (manifest.source?.system === 'azure_ad') {
    return 'azure_ad';
  }
  if (manifest.source?.system === 'google_workspace') {
    return 'google_workspace';
  }
  if (manifest.source?.system === 'bamboohr') {
    return 'bamboohr';
  }
  return null;
}
